import { isNil } from 'lodash';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import {
  getComponentIconWithFallback,
  getComponentLabelWithFallback,
  isFilterableComponent,
  isSortableComponent,
} from '@shared/scenes/workflows/utils';
import { i18n } from '@shared/util/i18n';
import { FilterOption, RelatedEntityPath, SortOption } from '../types';
import { canFilterComponentValue, getFilterScalarKeyOptions } from './filterScalarKey';
import { getRelatedEntityPathComponentIds, getRelatedEntityPathKey } from './relatedEntityPath';

/**
 * The filter menu entries for `components`.
 *
 * `isFilterable` is the server's answer about a component's **own value**, so it gates that entry
 * alone. A scalar representation doesn't read that value - a TIME_TRACKER's timer entries are not
 * filterable, but whether the timer is running is - so representations are offered whatever the
 * flag says. Without this, a component the server marks unfilterable would take its
 * representations down with it and they would never reach the menu.
 *
 * `relatedEntityPath` is passed for the cross-workflow case, where these are the fields of a
 * workflow reached through one or more related-card hops from this one.
 */
export function toFilterOptions(
  components: InputComponent[],
  relatedEntityPath?: RelatedEntityPath,
): FilterOption[] {
  return components.flatMap(component => {
    const options = toComponentOptions(component, relatedEntityPath);
    return isFilterableComponent(component)
      ? options
      : options.filter(option => !isNil(option.scalarKey));
  });
}

/**
 * The group menu entries for `components`. Unlike filters, grouping doesn't consult
 * `isFilterable` - callers pass an already groupable set (see `isGroupableComponent`).
 */
export function toGroupOptions(
  components: InputComponent[],
  relatedEntityPath?: RelatedEntityPath,
): FilterOption[] {
  return components.flatMap(component => toComponentOptions(component, relatedEntityPath));
}

/**
 * The sort menu entries for `components`. Sorting only ever orders a component by its own value -
 * there are no scalar representations to offer - so this is one entry per sortable component.
 */
export function toSortOptions(components: InputComponent[]): SortOption[] {
  return components.filter(isSortableComponent).map(component => ({
    id: component.id,
    type: component.type,
    label: getComponentLabelWithFallback(component),
    icon: getComponentIconWithFallback(component),
    component,
  }));
}

/**
 * The entries one component contributes: its own value, plus one per selectable scalar
 * representation of it (see `getFilterScalarKeyOptions`).
 *
 * The representations sit in the same flat list rather than nested under the component, at the
 * position the component itself occupies, so a component with representations reads as a small run
 * of sibling entries. A component whose own value has no editor contributes only its
 * representations, so it can't produce a row with no way to pick a value.
 */
function toComponentOptions(
  component: InputComponent,
  relatedEntityPath?: RelatedEntityPath,
): FilterOption[] {
  const label = getComponentLabelWithFallback(component);
  const base: FilterOption = {
    id: component.id,
    type: component.type,
    label,
    icon: getComponentIconWithFallback(component),
    component,
    relatedEntityPath,
  };

  const scalarOptions = getFilterScalarKeyOptions(component.type).map((option): FilterOption => ({
    ...base,
    scalarKey: option.scalarKey,
    label: i18n('card.actions.filters.scalarFieldLabel', {
      label,
      scalarLabel: option.label,
    }),
    icon: option.icon,
  }));

  return canFilterComponentValue(component.type) ? [base, ...scalarOptions] : scalarOptions;
}

/**
 * Distinguishes the entries a single component contributes. `id` alone repeats across them, so it
 * can't key a list or mark the selected one on its own - and the same field reached through
 * different related-card hops repeats it too, so the hops are part of the key.
 */
export function getFilterOptionKey(option: FilterOption): string {
  const pathKey = getRelatedEntityPathKey(
    getRelatedEntityPathComponentIds(option.relatedEntityPath),
  );
  return [pathKey, option.id, option.scalarKey].filter(Boolean).join(':');
}
